import { useEffect, useState } from "react";
import { fetchProjectMembers } from "@/lib/api";

// Small set of deterministic background colours so avatars without a picture still
// look distinct. Picked to complement the neon/sage/sky palette without overpowering.
const BG_PALETTE = [
  "#241623", "#7DAA92", "#63ADF2", "#9333ea",
  "#ea580c", "#0891b2", "#be185d", "#14532d",
];

function initials(email, name) {
  const n = (name || "").trim();
  if (n) {
    const parts = n.split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]).join("").toUpperCase();
  }
  const e = (email || "").trim();
  if (!e) return "?";
  const [local] = e.split("@");
  return local.slice(0, 2).toUpperCase();
}

function bgFor(email) {
  const s = email || "";
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return BG_PALETTE[h % BG_PALETTE.length];
}

function Avatar({ member, size = 28, zIndex }) {
  const style = {
    width: size,
    height: size,
    zIndex,
    background: member.avatar_url ? undefined : bgFor(member.email),
  };
  const title = [
    member.name || member.email,
    member.role ? `(${member.role})` : null,
  ].filter(Boolean).join(" ");
  return (
    <div
      className="rounded-full ring-2 ring-white flex items-center justify-center text-white text-[10px] font-semibold font-mono-ibm overflow-hidden -ml-2 first:ml-0"
      style={style}
      title={title}
      data-testid={`member-avatar-${member.email}`}
    >
      {member.avatar_url ? (
        <img src={member.avatar_url} alt="" className="w-full h-full object-cover" />
      ) : (
        <span>{initials(member.email, member.name)}</span>
      )}
    </div>
  );
}

// Renders up to maxVisible avatars (alphabetical by email) + a "+N" bubble for the rest.
// Accepts a pre-fetched `members` array, or a `projectId` (in which case it fetches itself).
export default function MemberAvatarStack({ projectId, members: initial, maxVisible = 4, size = 28 }) {
  const [members, setMembers] = useState(initial || []);
  const [loading, setLoading] = useState(!initial && !!projectId);

  useEffect(() => {
    if (initial) { setMembers(initial); return; }
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchProjectMembers(projectId);
        if (!cancelled) setMembers(list || []);
      } catch {
        /* silent — not worth interrupting the dashboard for a missing avatar strip */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, initial]);

  if (loading) {
    return (
      <div className="flex items-center" data-testid="member-avatars-loading">
        <div className="h-7 w-7 rounded-full bg-slate-100 animate-pulse" />
      </div>
    );
  }
  if (!members || members.length === 0) {
    return (
      <span className="text-[10px] font-mono-ibm uppercase tracking-widest text-slate-400">
        No members
      </span>
    );
  }
  const sorted = [...members].sort((a, b) => (a.email || "").localeCompare(b.email || ""));
  const visible = sorted.slice(0, maxVisible);
  const extra = sorted.length - visible.length;

  return (
    <div className="flex items-center" data-testid="member-avatars">
      {visible.map((m, i) => (
        <Avatar key={m.email} member={m} size={size} zIndex={20 - i} />
      ))}
      {extra > 0 && (
        <div
          className="rounded-full ring-2 ring-white bg-slate-100 text-slate-700 text-[10px] font-semibold font-mono-ibm flex items-center justify-center -ml-2"
          style={{ width: size, height: size }}
          title={`${extra} more member${extra === 1 ? "" : "s"}`}
          data-testid="member-avatars-overflow"
        >
          +{extra}
        </div>
      )}
    </div>
  );
}
